(() => {
  'use strict';

  // ═══════════════════════════════════════════════════════════════
  // CONFIG
  // ═══════════════════════════════════════════════════════════════

  const FIELD_ORDER = ['brand', 'model', 'version', 'generation', 'bodytype'];
  const FIELD_INDEX = FIELD_ORDER.reduce((acc, field, index) => {
    acc[field] = index;
    return acc;
  }, {});

  const CUSTOM_OPTION_VALUE = '__custom__';
  const DEFAULT_SUBMIT_URL = 'https://email-form-production.up.railway.app/api/inquiry';

  const TEXT_DATA_EL = document.getElementById('custom-form_data');
  const readText = (key, fallback) => TEXT_DATA_EL?.dataset[key] || fallback;

  const TEXT = {
    notFoundLabel: readText('notFoundLabel', 'Nie znalazłem — wpisz ręcznie'),
    addPhoto: readText('addPhoto', 'Dodaj'),
    photoPreviewAlt: readText('photoPreviewAlt', 'Podgląd zdjęcia'),
    removePhoto: readText('removePhoto', '×'),
    removePhotoAria: readText('removePhotoAria', 'Usuń zdjęcie'),
    formInvalid: readText('formInvalid', 'Uzupełnij wszystkie wymagane pola przed wysłaniem.'),
    loading: readText('loading', 'Wysyłanie...'),
    success: readText('success', 'Zgłoszenie zostało wysłane. Odezwiemy się najszybciej jak to możliwe.'),
    error: readText('error', 'Nie udało się wysłać zgłoszenia. Spróbuj ponownie za chwilę.'),
  };

  // --- Model-name translation (kept from the full car-select script) -------

  const patterns = {
    klasa: /^(\w+)\s+Klasa$/i,                      // A Klasa
    seriaNumber: /^Seria\s+(\d)$/i,                 // Seria 1
    seriaNumberM: /^Seria\s+(\dM)$/i,               // Seria 1M
    seriaMNumber: /^Seria\s+(M\d)$/i,               // Seria M5
    seriaGT: /^Seria\s+(\d\s+GT)$/i,                // Seria 6 GT
    seriaGranCoupe: /^Seria\s+(\d)\s+Gran Coupe$/i, // Seria 2 Gran Coupe
  };

  const stripSeriaPrefix = (_captures, original) => original.replace('Seria ', '');

  const translations = {
    de: {
      klasa: ([model]) => `${model} Klasse`,
      seriaNumber: ([num]) => `${num}er`,
      seriaNumberM: stripSeriaPrefix,
      seriaMNumber: stripSeriaPrefix,
      seriaGT: stripSeriaPrefix,
      seriaGranCoupe: ([num]) => `${num}er Gran Coupe`,
    },
    en: {
      klasa: ([model]) => `${model} Class`,
      seriaNumber: ([num]) => `Series ${num}`,
      seriaNumberM: stripSeriaPrefix,
      seriaMNumber: stripSeriaPrefix,
      seriaGT: stripSeriaPrefix,
      seriaGranCoupe: ([num]) => `Series ${num} Gran Coupe`,
    },
    fr: {
      klasa: ([model]) => `${model} Classe`,
      seriaNumber: ([num]) => `Série ${num}`,
      seriaNumberM: stripSeriaPrefix,
      seriaMNumber: stripSeriaPrefix,
      seriaGT: stripSeriaPrefix,
      seriaGranCoupe: ([num]) => `Série ${num} Gran Coupe`,
    },
  };

  const translateModelLabel = (input) => {
    const lang = document.documentElement.lang || 'pl';
    const original = input.trim();

    for (const [key, regex] of Object.entries(patterns)) {
      const match = original.match(regex);
      if (!match) continue;

      const rule = translations[lang]?.[key];
      if (!rule) continue;

      return rule(match.slice(1), original);
    }

    return original;
  };

  // ═══════════════════════════════════════════════════════════════
  // INIT
  // ═══════════════════════════════════════════════════════════════

  document.addEventListener('DOMContentLoaded', async () => {
    const form = document.getElementById('vehicle-form');
    if (!form) return;
    const formLoadedAtInput = form.querySelector('#form_loaded_at');
    if (formLoadedAtInput) formLoadedAtInput.value = String(Date.now());

    const vehicleWrapper = form.querySelector('.collection-filters__form');
    if (!vehicleWrapper) return;

    const selects = {
      brand: vehicleWrapper.querySelector('#car-select-form-brand'),
      model: vehicleWrapper.querySelector('#car-select-form-model'),
      version: vehicleWrapper.querySelector('#car-select-form-version'),
      generation: vehicleWrapper.querySelector('#car-select-form-generation'),
      bodytype: vehicleWrapper.querySelector('#car-select-form-body-type'),
    };

    const customInputs = {
      brand: vehicleWrapper.querySelector('#car-select-form-brand-custom'),
      model: vehicleWrapper.querySelector('#car-select-form-model-custom'),
      version: vehicleWrapper.querySelector('#car-select-form-version-custom'),
      generation: vehicleWrapper.querySelector('#car-select-form-generation-custom'),
      bodytype: vehicleWrapper.querySelector('#car-select-form-body-type-custom'),
    };

    if (!selects.brand || !selects.model || !selects.version || !selects.generation || !selects.bodytype) return;

    const placeholders = FIELD_ORDER.reduce((acc, field) => {
      acc[field] = selects[field].querySelector('option')?.textContent?.trim() || '';
      return acc;
    }, {});

    let manualModeFromIndex = -1; 

    // ─── Vehicle taxonomy ────────────────────────────────────────

    const VEHICLE_DATA_URL = vehicleWrapper.dataset.vehicleDataUrl;
    const VEHICLE_LOADER_URL = vehicleWrapper.dataset.vehicleLoaderUrl;

    const fetchLegacyVehicleData = () => {
      if (!VEHICLE_DATA_URL) {
        console.error('[vehicle-inquiry-form] Missing data-vehicle-data-url attribute.');
        return Promise.resolve(null);
      }

      return fetch(VEHICLE_DATA_URL, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
          return response.json();
        })
        .catch((error) => {
          console.error('[vehicle-inquiry-form] Failed to load vehicle taxonomy.', error);
          return null;
        });
    };

    // Shared taxonomy (same data as chat and finder) first, legacy file only as fallback
    const loadVehicleData = async () => {
      try {
        if (!window.MaxtonVehicleDataPromise && VEHICLE_LOADER_URL) await loadScriptWithTimeout(VEHICLE_LOADER_URL);
        const sharedData = await window.MaxtonVehicleDataPromise;
        if (Array.isArray(sharedData?.nodes)) return sharedData;
      } catch (error) {
        console.warn('[vehicle-inquiry-form] Shared vehicle taxonomy unavailable, using legacy data file.', error);
      }

      return fetchLegacyVehicleData();
    };

    window.__maxtonVehicleDataPromise = window.__maxtonVehicleDataPromise || loadVehicleData();
    const rawData = await window.__maxtonVehicleDataPromise;
    if (!rawData || !Array.isArray(rawData.nodes)) return;

    const carData = prepareVehicleTree(rawData);
    const selectionNodes = {};

    // ─── Hidden fields — the "output" of this whole selector ──────

    const hiddenFields = {};
    const HIDDEN_FIELD_KEYS = [...FIELD_ORDER, 'car_name'];

    const ensureHiddenField = (key) => {
      if (hiddenFields[key]) return hiddenFields[key];

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `vehicle_${key}`;
      form.appendChild(input);

      hiddenFields[key] = input;
      return input;
    };

    const getFieldValue = (fieldType) => {
      const customInput = customInputs[fieldType];
      if (customInput && !customInput.hidden) return customInput.value.trim();
      return selectionNodes?.[fieldType]?.label ?? '';
    };

    const syncHiddenFields = () => {
      const values = FIELD_ORDER.reduce((acc, field) => {
        acc[field] = getFieldValue(field);
        return acc;
      }, {});

      const carName = [values.brand, values.model, values.version, values.generation]
        .filter(Boolean)
        .join(' ');

      HIDDEN_FIELD_KEYS.forEach((key) => {
        ensureHiddenField(key).value = key === 'car_name' ? carName : values[key];
      });
    };

    // ─── Select rendering ───────────────────────────────────────

    function resetSelect(select, placeholder) {
      select.innerHTML = '';
      const placeholderOption = document.createElement('option');
      placeholderOption.value = '';
      placeholderOption.selected = true;
      placeholderOption.textContent = placeholder;
      select.appendChild(placeholderOption);

      select.disabled = true;
      select.required = false;
      select.hidden = false;
      select.parentNode.classList.add('hidden');
    }

    function populateSelect(select, placeholder, options) {
      select.innerHTML = '';

      const placeholderOption = document.createElement('option');
      placeholderOption.value = '';
      placeholderOption.selected = true;
      placeholderOption.textContent = placeholder;
      select.appendChild(placeholderOption);

      options.forEach((opt) => {
        const label = opt.node.type === 'model' ? translateModelLabel(opt.label) : opt.label;
        const option = document.createElement('option');
        option.value = String(opt.id);
        option.textContent = label;
        select.appendChild(option);
      });

      const customOption = document.createElement('option');
      customOption.value = CUSTOM_OPTION_VALUE;
      customOption.textContent = TEXT.notFoundLabel;
      select.appendChild(customOption);

      select.disabled = false;
      select.hidden = false;
      select.required = true; 
      select.parentNode.classList.remove('hidden');
    }

    const clearDownstream = (fromFieldType) => {
      FIELD_ORDER.slice(FIELD_INDEX[fromFieldType] + 1).forEach((field) => {
        delete selectionNodes[field];
        resetSelect(selects[field], placeholders[field]);
      });
    };

    const updateAvailabilityAfterSelection = (fromFieldType) => {
      FIELD_ORDER.slice(FIELD_INDEX[fromFieldType] + 1).forEach((field) => {
        const options = getOptionsForField(field, selectionNodes, carData);
        resetSelect(selects[field], placeholders[field]);
        if (options.length > 0) populateSelect(selects[field], placeholders[field], options);
      });
    };

    const setSelectionFromSelect = (fieldType) => {
      const id = Number(selects[fieldType].value);
      selectionNodes[fieldType] = Number.isFinite(id) ? carData.nodesById.get(id) : undefined;
      if (!selectionNodes[fieldType]) delete selectionNodes[fieldType];
    };

    // ─── Manual ("not found") fallback ─────────────────────────

    const enterManualMode = (fieldType) => {
      manualModeFromIndex = FIELD_INDEX[fieldType];
      selects[fieldType].required = false;
      const triggerInput = customInputs[fieldType];
      if (triggerInput) {
        triggerInput.hidden = false;
        triggerInput.required = true;
        triggerInput.value = '';
      }
      FIELD_ORDER.slice(manualModeFromIndex + 1).forEach((field) => {
        delete selectionNodes[field];

        const customInput = customInputs[field];
        if (customInput) {
          customInput.hidden = false;
          customInput.required = false;
          customInput.value = '';
        }

        selects[field].value = '';
        selects[field].disabled = true;
        selects[field].required = false;
        selects[field].hidden = true;
        selects[field].parentNode.classList.remove('hidden');
      });

      customInputs[fieldType]?.focus();
      syncHiddenFields();
    };

    const exitManualMode = () => {
      if (manualModeFromIndex === -1) return;

      FIELD_ORDER.slice(manualModeFromIndex).forEach((field) => {
        const customInput = customInputs[field];
        if (!customInput) return;
        customInput.hidden = true;
        customInput.required = false;
        customInput.value = '';
      });

      manualModeFromIndex = -1;
    };

    // ─── Wiring ─────────────────────────────────────────────────

    const handleSelectChange = (fieldType) => {
      if (selects[fieldType].value === CUSTOM_OPTION_VALUE) {
        enterManualMode(fieldType);
        return;
      }
      exitManualMode();
      setSelectionFromSelect(fieldType);
      clearDownstream(fieldType);
      updateAvailabilityAfterSelection(fieldType);
      syncHiddenFields();
    };

    FIELD_ORDER.forEach((field) => {
      selects[field].addEventListener('change', () => handleSelectChange(field));
    });

    Object.values(customInputs).forEach((input) => {
      input?.addEventListener('input', syncHiddenFields);
    });

    const brandOptions = getOptionsForField('brand', selectionNodes, carData);
    populateSelect(selects.brand, placeholders.brand, brandOptions);
    FIELD_ORDER.slice(1).forEach((field) => resetSelect(selects[field], placeholders[field]));
    initCustomSelects(FIELD_ORDER.map((field) => selects[field]));

    // ─── Other form modules ────────────────────────────────────

    initInterestedInToggle(form);
    const photoUploader = initPhotoUploader(form);
    const sectionProgress = initSectionProgress(form);

    // ─── Prefill from chat ─────────────────────────────────────

    applyChatPrefill(form, selects, carData);
    sectionProgress?.recompute();

    // ─── Submit ─────────────────────────────────────────────────

    initSubmitHandler(form, vehicleWrapper.dataset.submitUrl || DEFAULT_SUBMIT_URL, photoUploader, sectionProgress);
  });

  // ═══════════════════════════════════════════════════════════════
  // SCRIPT LOADING — used to start the shared vehicle taxonomy loader
  // when this page doesn't include it already
  // ═══════════════════════════════════════════════════════════════

  const SCRIPT_LOAD_TIMEOUT_MS = 8000;

  function loadScriptWithTimeout(src) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const timeout = window.setTimeout(() => reject(new Error(`Timed out loading ${src}`)), SCRIPT_LOAD_TIMEOUT_MS);

      script.src = src;
      script.addEventListener('load', () => { window.clearTimeout(timeout); resolve(); }, { once: true });
      script.addEventListener('error', () => { window.clearTimeout(timeout); reject(new Error(`Failed to load ${src}`)); }, { once: true });
      document.head.appendChild(script);
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // VEHICLE TREE HELPERS — pure functions, no DOM access
  // ═══════════════════════════════════════════════════════════════

  function prepareVehicleTree(rawData) {
    const nodes = (rawData.nodes ?? [])
      .map((node) => ({
        id: Number(node.id),
        parentId: Number(node.parentId),
        type: node.type ?? '',
        label: node.label ?? '',
      }))
      .filter((node) => Number.isFinite(node.id) && Number.isFinite(node.parentId));

    const nodesById = new Map();
    const childrenByParent = new Map();

    nodes.forEach((node) => {
      nodesById.set(node.id, node);
      if (!childrenByParent.has(node.parentId)) childrenByParent.set(node.parentId, []);
      childrenByParent.get(node.parentId).push(node);
    });

    childrenByParent.forEach((children) => {
      children.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    });

    const DEFAULT_BRANDS_ROOT_ID = 1876;

    return {
      rootId: Number(rawData.rootId) || DEFAULT_BRANDS_ROOT_ID,
      nodesById,
      childrenByParent,
    };
  }

  function formatOptions(nodes) {
    return [...nodes]
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))
      .map((node) => ({ id: node.id, label: node.label, node }));
  }

  function getOptionsForField(fieldType, selectionNodes, carData) {
    if (fieldType === 'brand') {
      return formatOptions(carData.childrenByParent.get(carData.rootId) ?? []);
    }

    if (fieldType === 'bodytype') return getBodyTypeOptions(selectionNodes, carData);

    const previousField = FIELD_ORDER[FIELD_INDEX[fieldType] - 1];
    const parentNode = selectionNodes?.[previousField];
    if (!parentNode) return [];

    const children = carData.childrenByParent.get(parentNode.id) ?? [];
    const typed = children.filter((child) => child.type === fieldType);
    return formatOptions(typed.length > 0 ? typed : children);
  }

  function getBodyTypeOptions(selectionNodes, carData) {
    const parentNode = selectionNodes?.generation || selectionNodes?.version;
    if (!parentNode) return [];

    const children = carData.childrenByParent.get(parentNode.id) ?? [];
    if (children.some((child) => child.type === 'generation')) return [];
    if (children.length === 0) return [];

    const options = [];
    children.forEach((node) => {
      const grandchildren = carData.childrenByParent.get(node.id) ?? [];
      if (grandchildren.length === 0) {
        options.push({ id: node.id, label: node.label, node });
        return;
      }
      grandchildren.forEach((child) => {
        options.push({ id: child.id, label: `${node.label} - ${child.label}`, node: child });
      });
    });

    return options.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  }

  // ═══════════════════════════════════════════════════════════════
  // CHAT PREFILL — the chat bot links here with ?source=chat&vehicle_id=…
  // The vehicle is restored by "clicking" through the native selects level
  // by level (same change events a user would fire), so the cascade, the
  // custom select UI and the section progress all stay in sync. Chat
  // context also goes into hidden fields so the backend can include it.
  // ═══════════════════════════════════════════════════════════════

  const CHAT_SOURCE = 'chat';
  const CHAT_MISSING_VEHICLE_TOPIC = 'missing-vehicle';
  const CHAT_SET_CATEGORY_TAG = 'SET';
  const INTERESTED_IN_VALUES = { set: 'maxton_set', product: 'product' };
  const CHAT_HIDDEN_FIELDS = {
    chat_source: 'source',
    chat_topic: 'topic',
    chat_category: 'category',
    chat_category_tag: 'category_tag',
    chat_vehicle: 'vehicle',
    chat_path: 'chat_path',
  };

  function readChatPrefill() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('source') !== CHAT_SOURCE) return null;

    const vehicleId = Number(params.get('vehicle_id'));
    return {
      params,
      vehicleId: Number.isSafeInteger(vehicleId) && vehicleId > 0 ? vehicleId : null,
      vehicleLabels: (params.get('vehicle') ?? '').split('›').map((label) => label.trim()).filter(Boolean),
      isMissingVehicle: params.get('topic') === CHAT_MISSING_VEHICLE_TOPIC,
    };
  }

  const normalizeLabel = (label = '') => label.toLowerCase().replace(/\s+/g, ' ').trim();

  function collectAncestorIds(nodeId, carData) {
    const ancestorIds = new Set();
    for (let node = carData.nodesById.get(nodeId); node; node = carData.nodesById.get(node.parentId)) {
      ancestorIds.add(node.id);
    }
    return ancestorIds;
  }

  function chooseSelectValue(select, value) {
    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Walks the selects top-down; findOption returns the option to pick for a level or nothing to stop
  function selectVehicleChain(selects, findOption) {
    let lastSelectedIndex = -1;

    for (const [index, field] of FIELD_ORDER.entries()) {
      const select = selects[field];
      if (select.disabled) break;

      const matchingOption = findOption([...select.options]);
      if (!matchingOption) break;

      chooseSelectValue(select, matchingOption.value);
      lastSelectedIndex = index;
    }

    return lastSelectedIndex;
  }

  function selectVehicleById(selects, vehicleId, carData) {
    const ancestorIds = collectAncestorIds(vehicleId, carData);
    return selectVehicleChain(selects, (options) => options.find((option) => ancestorIds.has(Number(option.value))));
  }

  // Fallback when the form's taxonomy doesn't know the id (e.g. older data file)
  function selectVehicleByLabels(selects, vehicleLabels, carData) {
    const remainingLabels = vehicleLabels.map(normalizeLabel);

    return selectVehicleChain(selects, (options) => {
      const expectedLabel = remainingLabels[0];
      if (!expectedLabel) return null;

      const matchingOption = options.find((option) => {
        const nodeLabel = carData.nodesById.get(Number(option.value))?.label;
        return [option.textContent, nodeLabel].some((label) => label && normalizeLabel(label) === expectedLabel);
      });
      if (matchingOption) remainingLabels.shift();
      return matchingOption;
    });
  }

  function openManualEntryAfter(selects, lastSelectedIndex) {
    const nextField = FIELD_ORDER[lastSelectedIndex + 1];
    const nextSelect = nextField && selects[nextField];
    if (!nextSelect || nextSelect.disabled) return;

    const hasCustomOption = [...nextSelect.options].some((option) => option.value === CUSTOM_OPTION_VALUE);
    if (hasCustomOption) chooseSelectValue(nextSelect, CUSTOM_OPTION_VALUE);
  }

  function writeChatHiddenFields(form, params) {
    Object.entries(CHAT_HIDDEN_FIELDS).forEach(([fieldName, paramName]) => {
      const value = params.get(paramName);
      if (!value) return;

      let input = form.querySelector(`input[type="hidden"][name="${fieldName}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = fieldName;
        form.appendChild(input);
      }
      input.value = value;
    });
  }

  function applyInterestedIn(form, params) {
    const select = form.querySelector('#interested_in');
    const productInput = form.querySelector('#interested_product');
    const categoryLabel = params.get('category');
    const categoryTag = params.get('category_tag');
    if (!select || !categoryLabel) return;

    const isSet = categoryTag?.toUpperCase() === CHAT_SET_CATEGORY_TAG;
    chooseSelectValue(select, isSet ? INTERESTED_IN_VALUES.set : INTERESTED_IN_VALUES.product);

    // "Other part" has no tag, the customer describes it in the product field
    if (isSet || !categoryTag || !productInput) return;
    productInput.value = categoryLabel;
    productInput.dispatchEvent(new Event('input', { bubbles: true }));
  }

  function applyChatPrefill(form, selects, carData) {
    const prefill = readChatPrefill();
    if (!prefill) return;

    writeChatHiddenFields(form, prefill.params);
    applyInterestedIn(form, prefill.params);

    const isKnownVehicleId = Boolean(prefill.vehicleId) && carData.nodesById.has(prefill.vehicleId);
    if (!isKnownVehicleId && !prefill.vehicleLabels.length) return;

    const lastSelectedIndex = isKnownVehicleId
      ? selectVehicleById(selects, prefill.vehicleId, carData)
      : selectVehicleByLabels(selects, prefill.vehicleLabels, carData);

    if (prefill.isMissingVehicle && lastSelectedIndex >= 0) openManualEntryAfter(selects, lastSelectedIndex);
  }

  // ═══════════════════════════════════════════════════════════════
  // CUSTOM SELECT — a div-based replacement for the vehicle <select>s.
  // The native <select> stays in the DOM untouched (still what carries the
  // value/name into FormData, still what the whole cascade above reads and
  // writes) — this only builds a custom-styled dropdown on top of it and
  // keeps the two in sync. The "not found" option lives in a footer OUTSIDE
  // the scrollable option list, so it's always visible at the bottom
  // regardless of scroll position — not possible with a native <select>,
  // where the browser/OS renders the popup and gives us no control over it.
  // ═══════════════════════════════════════════════════════════════

  function initCustomSelects(selectElements) {
    selectElements.filter(Boolean).forEach(enhanceSelectWithCustomUI);
    document.addEventListener('click', (event) => {
      document.querySelectorAll('.custom-select__panel:not([hidden])').forEach((panel) => {
        if (!panel.closest('.custom-select').contains(event.target)) panel.hidden = true;
      });
    });
  }

  function enhanceSelectWithCustomUI(select) {
    const wrapper = document.createElement('div');
    wrapper.className = 'custom-select';
    select.insertAdjacentElement('afterend', wrapper);
    select.classList.add('custom-select__native');
    select.tabIndex = -1;

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'custom-select__trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');

    const panel = document.createElement('div');
    panel.className = 'custom-select__panel';
    panel.hidden = true;

    const optionsList = document.createElement('ul');
    optionsList.className = 'custom-select__options';
    optionsList.setAttribute('role', 'listbox');

    const footer = document.createElement('div');
    footer.className = 'custom-select__footer';

    panel.append(optionsList, footer);
    wrapper.append(trigger, panel);

    const closePanel = () => {
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
    };

    const openPanel = () => {
      if (select.disabled) return;
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
    };

    trigger.addEventListener('click', () => (panel.hidden ? openPanel() : closePanel()));

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !panel.hidden) closePanel();
    });

    const chooseValue = (value) => {
      closePanel();
      if (select.value === value) return;
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const buildOptionButton = (option) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'custom-select__option';
      button.setAttribute('role', 'option');
      button.textContent = option.textContent;
      button.addEventListener('click', () => chooseValue(option.value));
      return button;
    };

    const sync = () => {
      const selectedOption = select.options[select.selectedIndex];
      trigger.textContent = selectedOption?.textContent ?? '';
      trigger.disabled = select.disabled;
      wrapper.hidden = select.hidden;
      trigger.classList.toggle('is-invalid', !select.checkValidity());

      optionsList.innerHTML = '';
      footer.innerHTML = '';

      [...select.options].forEach((option) => {
        if (option.value === '') return; // empty placeholder — trigger label already shows it
        const button = buildOptionButton(option);
        const target = option.value === CUSTOM_OPTION_VALUE ? footer : optionsList;
        target.appendChild(button);
      });
    };

    new MutationObserver(sync).observe(select, {
      attributes: true,
      attributeFilter: ['disabled', 'hidden', 'required'],
      childList: true,
    });
    select.addEventListener('change', sync);

    sync();
  }

  function initInterestedInToggle(form) {
    const select = form.querySelector('#interested_in');
    const wrapper = form.querySelector('#interested-product-wrapper');
    const input = form.querySelector('#interested_product');
    if (!select || !wrapper || !input) return;

    const sync = () => {
      const isProduct = select.value === 'product';
      wrapper.hidden = !isProduct;
      input.required = isProduct;
      if (!isProduct) input.value = '';
    };

    select.addEventListener('change', sync);
    form.addEventListener('reset', () => setTimeout(sync, 0));
    sync();
  }

  // ═══════════════════════════════════════════════════════════════
  // PHOTO UPLOADER — each "slot" is one <input type="file"> + its button.
  // Picking a file swaps the button for a preview + a remove button; filling
  // the last slot in the row grows the row by one more empty slot, so the
  // user always has somewhere to add another photo.
  // ═══════════════════════════════════════════════════════════════

  const ADD_ICON_SVG = `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-image-plus h-5 w-5" aria-hidden="true">
      <path d="M16 5h6"></path><path d="M19 2v6"></path>
      <path d="M21 11.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7.5"></path>
      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
      <circle cx="9" cy="9" r="2"></circle>
    </svg>${TEXT.addPhoto}
  `.trim();

  function initPhotoUploader(form) {
    const wrapper = form.querySelector('#photo-uploader-wrapper');
    if (!wrapper) return null;

    const REQUIRED_SLOT_COUNT = 2;
    const MAX_SLOT_COUNT = 6;

    const createAddButton = () => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'image_uploader__add';
      button.innerHTML = ADD_ICON_SVG;
      return button;
    };

    const createSlot = ({ required = false } = {}) => {
      const slot = document.createElement('div');
      slot.className = 'image_uploader';

      const input = document.createElement('input');
      input.type = 'file';
      input.name = 'photos[]';
      input.accept = 'image/*';
      input.required = required;

      slot.append(input, createAddButton());
      return slot;
    };

    const clearSlot = (slot) => {
      const input = slot.querySelector('input[type="file"]');
      input.value = '';

      if (slot.dataset.previewUrl) {
        URL.revokeObjectURL(slot.dataset.previewUrl);
        delete slot.dataset.previewUrl;
      }

      slot.querySelector('.image_uploader__preview')?.remove();
      slot.querySelector('.image_uploader__remove')?.remove();

      if (!slot.querySelector('.image_uploader__add')) slot.appendChild(createAddButton());
    };

    const showPreview = (slot, file) => {
      if (slot.dataset.previewUrl) URL.revokeObjectURL(slot.dataset.previewUrl);

      const objectUrl = URL.createObjectURL(file);
      slot.dataset.previewUrl = objectUrl;

      // Replacing a photo (clicking an already-filled slot again) fires
      // this same path — without removing the OLD preview/remove-button
      // first, they'd stay in the DOM and the new ones would just stack
      // on top instead of replacing them.
      slot.querySelector('.image_uploader__add')?.remove();
      slot.querySelector('.image_uploader__preview')?.remove();
      slot.querySelector('.image_uploader__remove')?.remove();

      const img = document.createElement('img');
      img.src = objectUrl;
      img.alt = TEXT.photoPreviewAlt;
      img.className = 'image_uploader__preview';

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'image_uploader__remove';
      removeButton.textContent = 'x';
      removeButton.setAttribute('aria-label', TEXT.removePhotoAria);
      removeButton.addEventListener('click', () => clearSlot(slot));

      slot.append(img, removeButton);
    };

    wrapper.addEventListener('change', (event) => {
      const input = event.target;
      if (!input.matches('input[type="file"]')) return;

      const slot = input.closest('.image_uploader');
      const file = input.files?.[0];
      if (!file) {
        clearSlot(slot);
        return;
      }

      showPreview(slot, file);

      if (slot === wrapper.lastElementChild && wrapper.children.length < MAX_SLOT_COUNT) {
        wrapper.appendChild(createSlot());
      }
    });

    const reset = () => {
      wrapper.innerHTML = '';
      for (let i = 0; i < REQUIRED_SLOT_COUNT; i += 1) {
        wrapper.appendChild(createSlot({ required: true }));
      }
    };
    const disableEmptySlots = () => {
      const emptyInputs = [...wrapper.querySelectorAll('input[type="file"]')].filter(
        (input) => input.files.length === 0
      );
      emptyInputs.forEach((input) => { input.disabled = true; });
      return () => emptyInputs.forEach((input) => { input.disabled = false; });
    };

    return { reset, disableEmptySlots };
  }

  // ═══════════════════════════════════════════════════════════════
  // SECTION PROGRESS — only the first <section> in the form starts usable.
  // Each next section unlocks once every section before it has all its
  // required fields filled in; a locked section is grayed out and its step
  // number is swapped for a lock icon via CSS (see .is-locked in the styles).
  // ═══════════════════════════════════════════════════════════════

  function initSectionProgress(form) {
    const sections = [...form.querySelectorAll(':scope > section')];
    if (sections.length === 0) return;

    const isFieldSatisfied = (field) => field.disabled || field.checkValidity();

    const isSectionComplete = (section) =>
      [...section.querySelectorAll('input, select, textarea')]
        .filter((field) => field.required)
        .every(isFieldSatisfied);

    const setSectionInteractive = (section, isInteractive) => {
      section.querySelectorAll('input, select, textarea, button').forEach((field) => {
        field.disabled = !isInteractive;
      });
    };

    const recompute = () => {
      let previousSectionsComplete = true;

      sections.forEach((section, index) => {
        const isFirstSection = index === 0;
        const shouldUnlock = isFirstSection || previousSectionsComplete;

        section.classList.toggle('is-locked', !shouldUnlock);

        if (!isFirstSection) setSectionInteractive(section, shouldUnlock);

        previousSectionsComplete = shouldUnlock && isSectionComplete(section);
      });
    };

    form.addEventListener('input', recompute);
    form.addEventListener('change', recompute);
    recompute();

    return { recompute };
  }

  // ═══════════════════════════════════════════════════════════════
  // SUBMIT — collect the whole form (vehicle fields, photos, contact info)
  // and POST it to the backend. Actually sending an email happens on that
  // backend (Node.js), never in the browser.
  // ═══════════════════════════════════════════════════════════════

  function initSubmitHandler(form, submitUrl, photoUploader, sectionProgress) {
    const submitButton = form.querySelector('button[type="submit"]');
    const originalButtonText = submitButton?.textContent ?? '';

    const statusEl = document.createElement('p');
    statusEl.className = 'vehicle-form-status';
    statusEl.hidden = true;
    form.insertAdjacentElement('afterend', statusEl);

    const setStatus = (message, kind) => {
      statusEl.textContent = message;
      statusEl.hidden = false;
      statusEl.dataset.status = kind; // 'success' | 'error' | 'loading' — style via CSS
    };


    const isFormValid = () =>
      [...form.querySelectorAll('input, select, textarea')]
        .filter((field) => field.required && !field.disabled)
        .every((field) => field.checkValidity());

    const getFirstInvalidField = () =>
      [...form.querySelectorAll('input, select, textarea')]
        .filter((field) => field.required && !field.disabled && !field.checkValidity())[0];

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      if (!isFormValid()) {
        const invalidField = getFirstInvalidField();
        if (invalidField) {
          invalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          invalidField.focus();
          invalidField.classList.add('invalid');
        }

        setStatus(TEXT.formInvalid, 'error');
        return;
      }

      if (submitButton) {
        submitButton.disabled = true;
        submitButton.hidden = true;
      }
      setStatus(TEXT.loading, 'loading');

      const reEnableEmptySlots = photoUploader?.disableEmptySlots();

      try {

        const response = await fetch(submitUrl, {
          method: 'POST',
          body: new FormData(form),
        });

        if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

        setStatus(TEXT.success, 'success');
        form.reset();
        photoUploader?.reset();
        sectionProgress?.recompute();

        form.hidden = true;
      } catch (error) {
        console.error('[vehicle-inquiry-form] Submit failed.', error);
        setStatus(TEXT.error, 'error');
      } finally {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.hidden = false;
          submitButton.textContent = originalButtonText;
        }

        reEnableEmptySlots?.();
      }
    });
  }
})();